import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import useForm from 'react-hook-form';
import { Grid, Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import cn from 'classnames';
import {
  setContract,
  getContractSubscription,
} from '../../../../redux/modules/recPayment';
import contractNumberSchema from '../../../../validations/contractNumberValidations';
import DataTable from '../../../../components/DataTable/DataTable';
import TitleTypography from '../../../../components/MUI/TitleTypography';
import {
  bulkUpdateBasket,
  clearBasket,
} from '../../../../redux/modules/basketReducer';
import useDeviceResize from '../../../../hooks/useDeviceResize';
import MobileTable from './MobileTable';
import {
  desktopTableColumnsForAttachCard,
  getContractStatus,
  maskContract,
  mobileTableColumnsForAttachCard,
  replaceMaskedContractNumber,
} from '../../../../helpers/recurringPaymentHelper';
import Loader from '../../../../components/Loaders/Loader';
import MainSwitch from '../../../../components/MUI/MainSwitch';
import MainButton from '../../../../components/MUI/MainButton';
import MainCheckbox from '../../../../components/MUI/MainCheckbox/MainCheckbox';
import Validation from '../../../../components/Validation/Validation';
import PolicySearchForm from '../../../../components/FormComponents/PolicySearchForm';
import AttachedCard from '../../../../components/Confirmation/PageContent/AttachedCard/AttachedCard';
import useRegisterValidationFields from '../../../../hooks/useRegisterValidationFields';
import useUnRegisterValidationFields from '../../../../hooks/useUnRegisterValidationFields';
import ScrollToBottomPage from '../../../../components/ScrollToBottomPage';

import recurringSearch from '../../../../styles/svgs/recurringSearch.svg';
import useStyles from '../../../MakeRecurringPaymentContainer/makeStyles';
import styles from '../../../MakeRecurringPaymentContainer/steps/RecurringPaymentScheduleStep/RecurringPaymentSchedule.module.scss';

import attachIcon from '../../../../img/svgs/payment/attach.svg';

const PolicyScheduleStep = ({ params }) => {
  const {
    setStepNavigationBox,
    handleOpenMobileSummary,
    handlePayClick,
    attachCardAgreement,
    setAttachCardAgreement,
    setAttachCardAgreementValue,
    attachCardAgreementErrors,
  } = params;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const urlParams = queryString.parse(search);
  const classes = useStyles();
  const { contract, isLoading } = useSelector((store) => store.recPayment, shallowEqual);
  const { basket } = useSelector((store) => store.basketReducer, shallowEqual);
  const [policyNumber, setPolicyNumber] = useState('');
  const isDeviceLgSize = useDeviceResize('lg');
  const isDeviceMdSize = useDeviceResize('md');
  const [showAllRows, setShowAllRows] = useState(false);
  const [shouldScroll, setShouldScroll] = useState(false);

  const {
    handleSubmit,
    register,
    unregister,
    errors,
    clearError,
    setValue,
    triggerValidation,
  } = useForm({
    validationSchema: contractNumberSchema,
    mode: 'onBlur',
  });

  useRegisterValidationFields(['policyNumber'], register);
  useUnRegisterValidationFields(['policyNumber'], unregister);

  useEffect(() => {
    if (urlParams.policyNumber) {
      dispatch(getContractSubscription({ policyNumber: urlParams.policyNumber }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (contract.policyNumber) {
      dispatch(bulkUpdateBasket([contract]));
    } else {
      dispatch(clearBasket());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, contract]);

  useEffect(() => {
    if (urlParams.policyNumber) {
      const maskedContract = maskContract(urlParams.policyNumber);
      setValue('policyNumber', maskedContract, true);
    } else if (contract.policyNumber) {
      const maskedContract = maskContract(contract.policyNumber);
      setValue('policyNumber', maskedContract, true);
    }
  }, [contract.policyNumber, setValue, urlParams.policyNumber]);

  useEffect(() => {
    if (!isEmpty(contract.tableRows) && !isDeviceLgSize) {
      setShouldScroll(true);
    }
  }, [contract.tableRows, isDeviceLgSize]);

  useEffect(() => {
    if (shouldScroll) {
      setShouldScroll(false);
    }
  }, [shouldScroll]);

  const isDTVisible = useMemo(
    () => !isLoading && !contract.isFullyPaid && !isEmpty(contract.tableRows),
    [contract.isFullyPaid, contract.tableRows, isLoading],
  );
  const filteredRows = useMemo(
    () => (showAllRows ? contract.tableRows : contract.tableRows?.filter((row) => !row.isDisabled)),
    [contract.tableRows, showAllRows],
  );

  const isDisabled = useMemo(() => (
      basket?.length === 0 || !isEmpty(contract.attachedCard) || contract.isFullyPaid
  ), [basket.length, contract]);

  useEffect(() => {
    setStepNavigationBox((
      <>
        <Grid container spacing={2} alignItems="center">
          {contract && !isEmpty(contract.attachedCard)
            ? (<Grid item xs={12}><AttachedCard attachedCard={contract.attachedCard} /></Grid>) : (
              <Grid item xs={12}>
                <Validation name="attachCardAgreement" errors={attachCardAgreementErrors}>
                  <div className={classes.mobileSummaryCheckbox}>
                    <label
                      className={cn({
                        [classes.agreementLabelDisabled]: isDisabled,
                      })}
                      htmlFor="attachCardAgreement"
                    >
                      <MainCheckbox
                        id="attachCardAgreement"
                        checked={attachCardAgreement}
                        onChange={(event) => {
                          setAttachCardAgreementValue('attachCardAgreement', event.target.checked, true);
                          setAttachCardAgreement(event.target.checked);
                        }}
                        color="primary"
                        style={{ marginRight: '0.5rem' }}
                        disabled={isDisabled}
                      />
                    </label>
                    <div className={cn({
                      [classes.agreementText]: true,
                      [classes.agreementLabelDisabled]: isDisabled,
                    })}
                    >
                      <p dangerouslySetInnerHTML={{
                        __html: t('automaticRecurringPayment.agreementText'),
                      }}
                      />
                    </div>
                  </div>
                </Validation>
              </Grid>
            )}
        </Grid>
        {contract && isEmpty(contract.attachedCard) && (
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <MainButton
              variant="contained"
              color="primary"
              onClick={handlePayClick}
              className={classes.buyButton}
              width="fullWidth"
              startIcon={<img src={attachIcon} alt="cardIcon" />}
              disabled={isDisabled || !attachCardAgreement}
            >
              {t('automaticRecurringPayment.attachCard')}
            </MainButton>
          </Grid>
        </Grid>
        )}
      </>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDeviceLgSize,
    basket,
    handleOpenMobileSummary,
    attachCardAgreement,
    attachCardAgreementErrors,
    contract,
  ]);

  const onSubmit = useCallback(
    (data) => {
      const newPolicyNumber = replaceMaskedContractNumber(data.policyNumber);
      if (newPolicyNumber === contract.policyNumber) {
        return;
      }
      const filteredBasketContract = basket?.find((c) => c.policyNumber === newPolicyNumber);
      if (filteredBasketContract) {
        dispatch(setContract(filteredBasketContract));
        return;
      }
      dispatch(
        getContractSubscription({
          policyNumber: newPolicyNumber,
        }),
      );
      urlParams.policyNumber = replaceMaskedContractNumber(data.policyNumber);
      history.push({ search: queryString.stringify(urlParams) });
      setPolicyNumber('');
    },
    [contract.policyNumber, basket, dispatch, urlParams, history, setPolicyNumber],
  );

  return (
    <>
      <ScrollToBottomPage shouldScroll={shouldScroll} />
      <Box>
        <div id={styles.checkPolicy}>
          <div>
            {isLoading && (
            <div>
              <Loader />
            </div>
            )}
            <TitleTypography component="div" className={styles.headerTitle}>
              {t('recurrPayment.title')}
            </TitleTypography>
            <form className={styles.form}>
              <PolicySearchForm
                formProps={{
                  handleSubmit,
                  errors,
                  clearError,
                  setValue,
                  triggerValidation,
                  onSubmit,
                }}
                policyNumber={policyNumber}
                setPolicyNumber={setPolicyNumber}
                isLoading={isLoading}
                gridSize={isDeviceLgSize ? 12 : 6}
              />
              <Grid item xs={isDeviceMdSize ? 10 : 11} />
              {contract.result && (
              <div className={styles.message}>{getContractStatus(contract, t)}</div>
              )}
            </form>
            {!isDTVisible && (
            <div className={styles.infoBlock}>
              <img src={recurringSearch} alt="" />
              <div className={styles.subText}>{t('automaticRecurringPayment.info')}</div>
            </div>
            )}
          </div>
          {isDTVisible && (
          <Grid item xs={12} className={styles.infoSection}>
            <div className={styles.policyNumber}>{maskContract(contract.policyNumber)}</div>
            <div className={styles.fullName}>
              <MainSwitch
                color="primary"
                size={isDeviceLgSize ? 'small' : 'medium'}
                checked={showAllRows}
                onChange={(event) => {
                  setShowAllRows(event.target.checked);
                }}
              />
              <label className={styles.previousPayments}>
                {t('recurrPayment.previousPayments')}
              </label>
            </div>
          </Grid>
          )}
          {isDTVisible && (
          <>
            {isDeviceLgSize ? (
              <MobileTable
                data={filteredRows}
                columns={mobileTableColumnsForAttachCard(t)}
              />
            ) : (
              <DataTable
                data={filteredRows}
                columns={desktopTableColumnsForAttachCard(t)}
                paginated={false}
              />
            )}
          </>
          )}
        </div>
      </Box>
    </>
  );
};

PolicyScheduleStep.propTypes = {
  params: PropTypes.shape({
    setStepNavigationBox: PropTypes.func.isRequired,
    handleOpenMobileSummary: PropTypes.func.isRequired,
    handlePayClick: PropTypes.func.isRequired,
    attachCardAgreement: PropTypes.bool,
    setAttachCardAgreement: PropTypes.func.isRequired,
    setAttachCardAgreementValue: PropTypes.func.isRequired,
    attachCardAgreementErrors: PropTypes.shape({
      attachCardAgreement: PropTypes.shape({}),
    }).isRequired,
  }).isRequired,
};

export default PolicyScheduleStep;
